import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function RFQDialog({ open, onOpenChange, product }) {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    country: "",
    quantity: "",
    message: "",
  });

  const reset = () => {
    setForm({
      company_name: "",
      contact_name: "",
      email: "",
      phone: "",
      country: "",
      quantity: "",
      message: "",
    });
    setDone(false);
  };

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        ...form,
        product_id: product?.id || null,
        product_name: product?.name || null,
        inquiry_type: "rfq",
      });
      setDone(true);
      toast.success("RFQ submitted — our trade desk will respond within 24 hours.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setTimeout(reset, 300);
      }}
    >
      <DialogContent
        className="max-w-2xl bg-white p-0 overflow-hidden"
        data-testid="rfq-dialog"
      >
        {!done ? (
          <>
            <div className="bg-[var(--rsi-purple)] text-white px-6 py-5">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-extrabold tracking-tight text-white">
                  Request for Quote
                </DialogTitle>
                <DialogDescription className="text-white/80 text-sm">
                  {product
                    ? `For ${product.name} — share specs and quantity, we'll respond with a tailored quote.`
                    : "Tell us what you're sourcing — we'll respond with verified suppliers, MOQ, and FOB pricing."}
                </DialogDescription>
              </DialogHeader>
            </div>
            <form onSubmit={submit} className="p-6 grid gap-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input
                    id="company_name"
                    required
                    value={form.company_name}
                    onChange={handleChange("company_name")}
                    data-testid="rfq-company-name"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_name">Contact Name *</Label>
                  <Input
                    id="contact_name"
                    required
                    value={form.contact_name}
                    onChange={handleChange("contact_name")}
                    data-testid="rfq-contact-name"
                    className="mt-1.5"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Business Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={handleChange("email")}
                    data-testid="rfq-email"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone (with country code)</Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={handleChange("phone")}
                    data-testid="rfq-phone"
                    className="mt-1.5"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="country">Destination Country</Label>
                  <Input
                    id="country"
                    value={form.country}
                    onChange={handleChange("country")}
                    data-testid="rfq-country"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="quantity">Quantity / MOQ</Label>
                  <Input
                    id="quantity"
                    placeholder={product?.moq || "e.g. 1,000 units"}
                    value={form.quantity}
                    onChange={handleChange("quantity")}
                    data-testid="rfq-quantity"
                    className="mt-1.5"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  required
                  rows={4}
                  placeholder="Share product specs, target price, port of discharge, timeline…"
                  value={form.message}
                  onChange={handleChange("message")}
                  data-testid="rfq-message"
                  className="mt-1.5"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full disabled:opacity-60"
                data-testid="rfq-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                  </>
                ) : (
                  "Submit RFQ"
                )}
              </button>
            </form>
          </>
        ) : (
          <div className="p-10 text-center" data-testid="rfq-success">
            <div className="mx-auto h-16 w-16 rounded-full bg-[var(--rsi-green)]/10 grid place-items-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-[var(--rsi-green)]" />
            </div>
            <h3 className="font-display text-2xl font-extrabold tracking-tight text-[var(--rsi-navy)]">
              RFQ Received
            </h3>
            <p className="text-gray-600 mt-2 max-w-md mx-auto">
              Thank you. Our trade desk has received your request and will respond within 24 business hours.
            </p>
            <button
              onClick={() => onOpenChange(false)}
              className="btn-ghost mt-6"
              data-testid="rfq-close"
            >
              Close
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
